import { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack,
  Switch, TextField, Typography,
} from '@mui/material';
import { useRepointChannelHead } from '../../api/queries';
import type { ReleaseCatalogueEntry } from '../../api/queries';

/**
 * PR 2 of the software-rollout work — promote a release to a channel
 * head. Users pick the channel (stable | beta) and an optional reason
 * (recorded in the audit log). The server enforces state-vs-channel
 * compatibility: stable channels require state=released; beta channels
 * also accept state=in_test (so candidates can hit beta sites first).
 *
 * Single mutation: PUT /api/v1/channel-heads/{target}/{channel}. No
 * state transition is performed here — if the release isn't in a
 * promotable state for the chosen channel the server returns 400
 * with a code the dialog surfaces verbatim.
 */
export function PromoteToHeadDialog({
  release, defaultChannel, open, onClose, onSuccess,
}: {
  release: ReleaseCatalogueEntry | null;
  defaultChannel: 'stable' | 'beta';
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const [channel, setChannel] = useState<'stable' | 'beta'>(defaultChannel);
  const [reason,  setReason]  = useState('');
  // PR 3 staged-rollout pacing — opt-in. Default off = instant fleet-wide
  // flip (current behaviour). When on, the head still updates immediately
  // but SCs converge in batches per the server-side eligibility resolver.
  const [staged, setStaged]   = useState(false);
  const [batchSize, setBatchSize] = useState(50);
  const [batchIntervalMin, setBatchIntervalMin] = useState(10);
  const repoint = useRepointChannelHead();

  if (!release) return null;

  const reset = () => {
    setChannel(defaultChannel); setReason('');
    setStaged(false); setBatchSize(50); setBatchIntervalMin(10);
    repoint.reset();
  };
  const close = () => { reset(); onClose(); };

  const submit = async () => {
    try {
      await repoint.mutateAsync({
        target:    release.target,
        channel,
        releaseId: release.id,
        reason:    reason.trim() || undefined,
        rolloutBatchSize:            staged ? batchSize : null,
        rolloutBatchIntervalSeconds: staged ? batchIntervalMin * 60 : null,
      });
      onSuccess?.();
      close();
    } catch {
      /* error surfaces via repoint.error */
    }
  };

  // The server compatibility rules — mirrored client-side so the user
  // sees the constraint before they click submit. Server is still the
  // source of truth.
  const stableAllowed = release.state === 'released';
  const betaAllowed   = release.state === 'released' || release.state === 'in_test';
  const channelOk     = channel === 'stable' ? stableAllowed : betaAllowed;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Promote to channel head · {release.target}@{release.version}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Sets the current head for the chosen channel to <strong>{release.displayName}</strong>.
            Every site controller on that channel will pull this release within ~60 s.
          </Typography>

          <FormControl size="small" fullWidth>
            <InputLabel>Channel</InputLabel>
            <Select<'stable' | 'beta'>
              label="Channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            >
              <MenuItem value="stable">Stable (production fleet)</MenuItem>
              <MenuItem value="beta">Beta (sites flagged for beta testing)</MenuItem>
            </Select>
          </FormControl>

          {!channelOk && (
            <Alert severity="warning" variant="outlined">
              {channel === 'stable'
                ? <>Stable accepts only <code>released</code> artefacts. This release is <code>{release.state}</code> — promote it to <code>released</code> first.</>
                : <>Beta accepts <code>released</code> or <code>in_test</code> artefacts. This release is <code>{release.state}</code>.</>}
            </Alert>
          )}

          <TextField
            label="Reason (recorded in audit log)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional — why is this being promoted now?"
          />

          {/* PR 3 — staged rollout pacing */}
          <Stack spacing={1} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <FormControlLabel
              control={<Switch checked={staged} onChange={(e) => setStaged(e.target.checked)} />}
              label="Stage the rollout (recommended for large fleets)"
            />
            {staged && (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Batch size (SCs per wave)"
                    size="small"
                    type="number"
                    value={batchSize}
                    onChange={(e) => setBatchSize(Math.max(1, Number(e.target.value) || 0))}
                    inputProps={{ min: 1, max: 1000 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Interval (minutes between waves)"
                    size="small"
                    type="number"
                    value={batchIntervalMin}
                    onChange={(e) => setBatchIntervalMin(Math.max(0, Number(e.target.value) || 0))}
                    inputProps={{ min: 0, max: 720 }}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Each SC is assigned a deterministic batch (stable hash of sc_id + rollout_id).
                  SCs in batch 0 get the new release immediately; batch 1 at {batchIntervalMin} min;
                  and so on. An SC offline at its batch boundary picks up on its next online poll.
                  Pause/resume/cancel from the rollouts dashboard.
                </Typography>
              </Stack>
            )}
          </Stack>

          {repoint.error && (
            <Alert severity="error" variant="outlined">
              {(repoint.error as { message?: string }).message ?? 'Promote failed.'}
            </Alert>
          )}

          <Box sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: 1, fontSize: 12, color: 'text.secondary' }}>
            <strong>Note:</strong> This is the head pointer only. SCs auto-pull within ~60 s
            via <code>RuntimeFetcher</code>. To stage the rollout in waves (recommended for
            large fleets), use the staged-rollout flow (PR 3 — pending).
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={repoint.isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!channelOk || repoint.isPending}
        >
          {repoint.isPending ? 'Promoting…' : `Promote to ${channel}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
